import React, { useState } from "react";
import { Country, CountryListRequest } from "../Types/countryTypes";
import { useGetCountriesListQuery } from "../Service/extendedCoutriesApi";
import { useDeleteCountryMutation } from "../Service/countriesApi";

import DataTable, { ColumnConfig } from "../../Shared/Components/DataTable";
import ActionButtons from "../../Shared/Components/ActionButtons";
import CountrySearchForm from "../Components/CountrySearchForm";
import { CountriesRoutes } from "../../../Router/path";
import { useNavigate } from "react-router-dom";
import CustomCard from "../../Shared/Components/CustomCard/CustomCard";
import InfoIcon from '@mui/icons-material/Info';
import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useDeleteSelectedItems } from '../../Shared/Hooks/useDeleteSelectedItems';
import { useCreateRedirect } from '../../Shared/Hooks/useCreateRedirect';

const Countries = () => {
    const [filters, setFilters] = React.useState({
        name: "",
    });
    const [page, setPage] = useState(0); // zero-based index
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedCountryIds, setSelectedCountryIds] = useState<number[]>([]);

    const { data, isLoading, isError, error } = useGetCountriesListQuery({
        searchPhrase: filters.name,
        pageNumber: page + 1, // API 1-based
        pageSize: rowsPerPage,
    } as CountryListRequest);

    const [deleteCountry] = useDeleteCountryMutation();

    const handleDeleteSelected = useDeleteSelectedItems<number>(
        selectedCountryIds,
        (id) => deleteCountry(id).unwrap(),
        () => setSelectedCountryIds([])
    );
    const handleCreate = useCreateRedirect(CountriesRoutes.create);

    const columns: ColumnConfig<Country>[] = [
        { key: "id", label: "ID" },
        { key: "name", label: "Nazwa" },
        {
            key: "published",
            label: "Opublikowany",
            render: (row) => (
                <input type="checkbox" disabled checked={row.published} />
            ),
        },
    ];
    const navigate = useNavigate();

    return (
        <>
            <CustomCard
                icon={<InfoIcon fontSize="large" color="primary" />}
                title="Kraje"
                subheader="Zarządzaj dostępnymi krajami w systemie"
                sx={{ mb: 4 }}
            >
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" gutterBottom>
                        Na tej stronie możesz dodać, edytować i publikować kraje.
                        Aby opublikować kraj, najpierw musisz wprowadzić podstawowe informacje
                        i dodać co najmniej jedno zdjęcie.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Skorzystaj z przycisków poniżej, żeby dodać nowy kraj, usunąć zaznaczone lub wybierz kraj z listy,
                        aby go edytować.
                    </Typography>
                </Box>
                <ActionButtons
                    onCreate={handleCreate}
                    onDeleteSelected={handleDeleteSelected}
                />
                <CountrySearchForm onSearch={setFilters} />
            </CustomCard>
            <CustomCard>
            <DataTable
                columns={columns}
                data={data?.items || []}
                isLoading={isLoading}
                isError={isError}
                errorMessage={(error as any)?.data?.message}
                getRowId={(row) => row.id}
                onEdit={(id) => {
                    const item = data?.items.find((item) => item.id === id);
                    if (item) {
                        navigate(
                            CountriesRoutes.update.replace(":countryId", item.id.toString())
                        );
                    }
                }}
                page={page}
                rowsPerPage={rowsPerPage}
                totalCount={data?.totalCount || 0}
                onPageChange={(newPage) => setPage(newPage)}
                onRowsPerPageChange={(newSize) => {
                    setRowsPerPage(newSize);
                    setPage(0);
                }}
                selected={selectedCountryIds}
                onSelectionChange={(selectedIds) => {
                    setSelectedCountryIds(selectedIds);
                }}
            />
            </CustomCard>
        </>
    );
};

export default Countries;
